import { createAction } from 'redux-actions';
import Section from 'pxc-editor-core/entities/Section';
import {
  getAllLayers,
  getSortedSelectedSections,
  getSortedSectionsArray,
  getSelectedSections,
} from 'pxc-editor-core/helpers/schema';
import { createSectionSmart } from 'pxc-editor-core/helpers/schema';
import {
  EDIT_SECTION,
  SECTION_MIRROR,
  ADD_SECTION,
  ADD_SECTIONS,
  ADD_SELECTED_SECTIONS,
  SPLIT_SECTIONS,
  ADD_NEW_SECTIONS,
  DELETE_SELECTED_SECTIONS,
  CLEAR_SELECTED_SECTIONS,
  CLEAR_ALL_SELECTED_SECTIONS,
  KEY_ARROW_LEFT,
  KEY_ARROW_RIGHT,
  KEY_ARROW_UP,
  KEY_ARROW_DOWN,
} from '@/constants';
import { getFirstSelectedSection, getSelectedSection, getCurrentPageShields, getAllShields, convertImageToBlackAndWhite } from '@/helpers';
import { checkPageContentLength } from './pageResize';
import { checkSyncSlaveShieldByMasterShield, checkSyncSlaveShieldBySection, overridePageAction } from './page';
import { createSnapshotThunk } from './undoRedo';
import {
  setShowLoader,
  handleEndlessTextInput,
  deleteSectionById,
  setPageChildrenBasedOnContent,
  changePageChildrenLength,
  deleteSelectedSections,
  handleSectionClick,
} from './main';
import { setGlobalSectionSettingsActionThunk } from '@/store/actions/app-settings';

export const deleteSelectedSectionsAction = payload => createAction(DELETE_SELECTED_SECTIONS, () => payload)();

export const addSectionAction = payload => createAction(ADD_SECTION, () => payload)();

const splitSectionsAction = payload => createAction(SPLIT_SECTIONS, () => payload)();

export const addSectionsAction = payload => {
  return createAction(ADD_SECTIONS, () => payload)();
};

export const addNewSectionsAction = payload => {
  return createAction(ADD_NEW_SECTIONS, () => payload)();
};

export const editSection = section => {
  return createAction(EDIT_SECTION, () => section)();
};

export const setBarcodeInSection = (codeData, isWebcomponent = false) => dispatch => {
  dispatch(updateSelectedSectionsProp('code', codeData, isWebcomponent));
};

export const setImageInSection = (image, isWebcomponent = false) => dispatch => {
  dispatch(updateSelectedSectionsProp('image', image, isWebcomponent));
};

export const setGlyphInSection = (glyph, isWebcomponent = false) => dispatch => {
  dispatch(updateSelectedSectionsProp('symbol', glyph, isWebcomponent));
};

export const addSectionAngle = (dAlpha, isWebcomponent = false) => dispatch => dispatch(updateSelectedSectionsProp('angle', dAlpha, isWebcomponent));

export const addSectionMirror = (coord, isWebcomponent = false) => dispatch => {
  dispatch(updateSelectedSectionsProp(SECTION_MIRROR, coord, isWebcomponent));
};

export const setTextInSection = (sectionId, text, isUndoable = true, styleTemplate = null) => (dispatch, getState) => {
  const { core: { pages, selWizard } } = getState();
  const { section, page } = getAllLayers(sectionId, pages);
  if (section) {
    const changedSection = Section.fromDict(section)
      .clone()
      .setText(text)
      .setGlyph(null)
      .setCode(null)
      .setImage(null)
      .setShape(null);

    if (styleTemplate) {
      changedSection
        .setFillColor(styleTemplate.fillColor)
        .setFontSize(styleTemplate.fontSize)
        .setBold(styleTemplate.isBold)
        .setItalic(styleTemplate.isItalic)
        .setUnderline(styleTemplate.isUnderline)
        .setFont(styleTemplate.font)
        .setHorizontalAlignment(styleTemplate.horizontalAlignment)
        .setVerticalAlignment(styleTemplate.verticalAlignment)
        .setFontColor(styleTemplate.fontColor)
        .setBackgroundState(styleTemplate.hasBackground)
        .setSymbolFont(styleTemplate.symbolFont)
        .setAngle(styleTemplate.angle)
        .setMirrorX(styleTemplate.mirrorX)
        .setMirrorY(styleTemplate.mirrorY)
        .setGlyphFillColor(styleTemplate.glyphFillColor)
        .setBarcodeColor(styleTemplate.barcodeColor)
        .setShapeStokeColor(styleTemplate.shapeStrokeColor)
        .setShapeFillColor(styleTemplate.shapeFillColor);
    }

    dispatch(createSnapshotThunk({ isUndoable }));
    dispatch(editSection(changedSection.toDict()));

    if (selWizard?.application_type === 'CABLE_FLAG') {
      setTimeout(() => {
        dispatch(handleEndlessTextInput(changedSection.id, false));
        if (selWizard?.data?.isCableFlagSync) dispatch(checkSyncSlaveShieldBySection(changedSection));
        dispatch(changePageChildrenLength(page.uid));
      }, 0);
    } else {
      setTimeout(() => {
        dispatch(handleEndlessTextInput(changedSection.id, false));
      }, 0);
    }
  }
};

export const setContentInSection = payload => (dispatch, getState) => {
  const { sectionId, content } = payload;
  const { core: { pages } } = getState();
  const { section } = getAllLayers(sectionId, pages);

  if (section) {
    dispatch(
      editSection({
        ...section,
        sourceSectionId: section.id,
        hasBackground: content.hasBackground,
        fillColor: content.fillColor,
        angle: content.angle,
        mirrorX: content.mirrorX,
        mirrorY: content.mirrorY,
        text: content.text,
        fontSize: content.fontSize,
        isBold: content.isBold,
        isItalic: content.isItalic,
        isUnderline: content.isUnderline,
        font: content.font,
        horizontalAlignment: content.horizontalAlignment,
        verticalAlignment: content.verticalAlignment,
        fontColor: content.fontColor,
        code: content.code,
        barcodeColor: content.barcodeColor,
        symbolFont: content.symbolFont,
        glyph: content.glyph,
        glyphFillColor: content.glyphFillColor,
        image: content.image,
        shape: content.shape,
      }),
    );
  }
};

// TODO: fix double call when typing is happening via 'Tab' key
export const setTextToSelectedSection = (text, isUndoable = true) => (dispatch, getState) => {
  const { core: { sectionsSelection, pages } } = getState();
  let selectedSection = getFirstSelectedSection(sectionsSelection, pages);
  if (selectedSection) {
    dispatch(setTextInSection(selectedSection.id, text, isUndoable));
  }
};


const handleMoveSection = (sectionId, x, y, isUndoable, dispatch, getState) => {
  const { core: { pages, shieldsSelection, selWizard } } = getState();
  const { section, page } = getAllLayers(sectionId, pages);
  const selectedShield = shieldsSelection[section.shieldId];
  if (section && selectedShield) {
    const movedSection = Section.fromDict(section)
      .clone()
      .setX(x)
      .setY(y)
      .setChanged(true);

    dispatch(createSnapshotThunk({ isUndoable }));
    dispatch(editSection(movedSection.toDict()));

    setTimeout(() => {
      if (selWizard?.application_type === 'CABLE_FLAG') {
        if (selWizard?.data?.isFlagLengthAuto) {
          dispatch(checkPageContentLength(page.uid));
        }
        dispatch(checkSyncSlaveShieldByMasterShield(selectedShield));
      } else {
        dispatch(checkPageContentLength(page.uid));
      }
    }, 0);
  }
};

export const moveSection = (sectionId, { dx, dy }, isUndoable = true) => (dispatch, getState) => {
  const { core: { pages } } = getState();
  const { section } = getAllLayers(sectionId, pages);

  handleMoveSection(sectionId, section.x + dx, section.y + dy, isUndoable, dispatch, getState);
};

export const alignSectionContent = (sectionId, { dx, dy }, isUndoable = true) => (dispatch, getState) => {
  handleMoveSection(sectionId, dx, dy, isUndoable, dispatch, getState);
};

export const resizeSection = (sectionId, { dx, dy, dw, dh }, isUndoable = true) => (dispatch, getState) => {
  const { core: { pages, shieldsSelection, selWizard } } = getState();
  const { section, page } = getAllLayers(sectionId, pages);
  const selectedShield = shieldsSelection[section.shieldId];
  if (section && selectedShield) {
    const { x, y, width, height } = section;
    const resizedSection = Section.fromDict(section)
      .clone()
      .setX(x + dx)
      .setY(y + dy)
      .setWidth(width + dw)
      .setHeight(height + dh)
      .setChanged(true);

    dispatch(createSnapshotThunk({ isUndoable }));  
    dispatch(editSection(resizedSection.toDict()));

    setTimeout(() => {
      if (selWizard?.application_type === 'CABLE_FLAG') {
        if (selWizard?.data?.isFlagLengthAuto) {
          dispatch(checkPageContentLength(page.uid));
        }
        dispatch(checkSyncSlaveShieldByMasterShield(selectedShield));
      } else {
        dispatch(checkPageContentLength(page.uid));
      }
    }, 0);
  }
};

export const deleteSectionContent = (sectionId, isUndoable = true) => (dispatch, getState) => {
  const { core: { pages } } = getState();
  if (sectionId) {
    const { section } = getAllLayers(sectionId, pages);
    const deletedContentSelectedSection = Section.fromDict(section)
      .clone()
      .setText('')
      .setGlyph(null)
      .setCode(null)
      .setImage(null)
      .setShape(null);

    dispatch(createSnapshotThunk({ isUndoable }));
    dispatch(editSection(deletedContentSelectedSection.toDict()));

    setTimeout(() => {
      dispatch(checkSyncSlaveShieldBySection(deletedContentSelectedSection));
    }, 10);
  }
};

export const updateSelectedSectionsProps = (changedProps, isUndoable = true) => (dispatch, getState) => {
  const { core: { sectionsSelection, pages, pagesSelection } } = getState();
  const [currentPageUid] = pagesSelection;
  const currentPage = pages[currentPageUid];
  const sectionsToFormat = getSortedSelectedSections({
    sectionsSelection,
    page: currentPage
  });
  const [firstSection] = sectionsToFormat;

  dispatch(createSnapshotThunk({ isUndoable }));

  if (!!firstSection) {
    const { shield } = getAllLayers(firstSection.shieldId, pages);
    if (!shield?.isMatrix) {
      dispatch(setGlobalSectionSettingsActionThunk({ ...firstSection, ...changedProps }));
    }
  }

  for (const section of sectionsToFormat) {
    dispatch(updateSectionProps(section, changedProps, false));
  }
};

export const updateSectionProps = (section, changedProps, isUndoable = true) => (dispatch, getState) => {
  const state = getState();
  let changedSection = Section.fromDict(section);

  for (const propName in changedProps) {
    changedSection = updateSectionPropSimple(changedSection, propName, changedProps[propName], state);
  }

  dispatch(createSnapshotThunk({ isUndoable }));
  dispatch(editSection(changedSection.toDict()));
  dispatch(checkSyncSlaveShieldBySection(changedSection));
};

export const updateSelectedSectionsProp = (changedProp, value) => (dispatch, getState) => {
  dispatch(createSnapshotThunk());

  const { core: { sectionsSelection, pages, pagesSelection } } = getState();
  const [currentPageUid] = pagesSelection;
  const currentPage = pages[currentPageUid];
  const sectionsToFormat = getSortedSelectedSections({
    sectionsSelection,
    page: currentPage
  });

  for (const section of sectionsToFormat) {
    dispatch(updateSectionProp(section, changedProp, value, false));
  }
};

export const updateSectionProp = (section, changedProp, value, isUndoable = true) => (dispatch, getState) => {
  const state = getState();
  const changedSection = updateSectionPropSimple(Section.fromDict(section), changedProp, value, state);
  const { core: { pages } } = state;
  const { page } = getAllLayers(section.id, pages);

  dispatch(createSnapshotThunk({ isUndoable }));
  dispatch(editSection(changedSection.toDict()));
  dispatch(setPageChildrenBasedOnContent(page.uid));
  dispatch(checkSyncSlaveShieldBySection(changedSection));
};

const updateSectionPropSimple = (section, changedProp, value, state) => {
  const { core: { pages, isMultiColored, paperColor, ribbonColor } } = state;
  const changedSection = section.clone();

  switch (changedProp) {
    case 'isBold': {
      changedSection.setBold(value);
      break;
    }
    case 'isItalic': {
      changedSection.setItalic(value);
      break;
    }
    case 'isUnderline': {
      changedSection.setUnderline(value);
      break;
    }
    case 'fontSize': {
      changedSection.setFontSize(value);
      break;
    }
    case 'fontWidthFactor':
      changedSection.setFontWidthFactor(value);
      break;

    case 'lineSpaceFactor':
      changedSection.setLineSpaceFactor(value);
      break;

    case 'font': {
      changedSection.setFont(value);
      break;
    }
    case 'fontColor': {
      changedSection.setFontColor(value);
      if (!isMultiColored && (value === paperColor || value === ribbonColor)) {
        changedSection
          .setFillColor(value === paperColor ? ribbonColor : paperColor)
          .setFontColor(value)
          .setGlyphFillColor(value)
          .setBarcodeColor(value);
      }
      break;
    }
    case 'verticalAlignment': {
      if (changedSection.glyph) changedSection.glyph.verticalAlignment = String(value).toUpperCase();
      if (changedSection.image) changedSection.image.verticalAlignment = String(value).toUpperCase();
      changedSection.setVerticalAlignment(value);
      break;
    }
    case 'horizontalAlignment': {
      if (changedSection.glyph) changedSection.glyph.horizontalAlignment = String(value).toUpperCase();
      if (changedSection.image) changedSection.image.horizontalAlignment = String(value).toUpperCase();
      changedSection.setHorizontalAlignment(value);
      break;
    }
    case 'hasBackground': {
      changedSection.setBackgroundState(value);
      break;
    }
    case 'fillColor': {
      changedSection.setFillColor(value);
      if (!isMultiColored && (value === paperColor || value === ribbonColor)) {
        const corrRibbon = value === paperColor ? ribbonColor : paperColor;
        changedSection
          .setFillColor(value)
          .setFontColor(corrRibbon)
          .setGlyphFillColor(corrRibbon)
          .setBarcodeColor(corrRibbon);
      }
      break;
    }
    case 'symbolFont': {
      changedSection.setSymbolFont(value);
      break;
    }
    case 'symbol': {
      if (value && !value.verticalAlignment) {
        value.verticalAlignment = String(changedSection.verticalAlignment).toUpperCase() || 'MIDDLE';
      }
      if (value && !value.horizontalAlignment) {
        value.horizontalAlignment = String(changedSection.horizontalAlignment).toUpperCase() || 'CENTER';
      }
      changedSection
        .setGlyph(value)
        .setText('')
        .setCode(null)
        .setImage(null)
        .setShape(null);
      break;
    }
    case 'glyphFillColor': {
      changedSection.setGlyphFillColor(value);
      if (!isMultiColored && (value === paperColor || value === ribbonColor)) {
        changedSection
          .setFillColor(value === paperColor ? ribbonColor : paperColor)
          .setFontColor(value)
          .setGlyphFillColor(value)
          .setBarcodeColor(value);
      }
      break;
    }
    case 'code': {
      changedSection
        .setCode(value)
        .setText('')
        .setGlyph(null)
        .setImage(null)
        .setShape(null);
      break;
    }
    case 'barcodeColor': {
      changedSection.setBarcodeColor(value);
      break;
    }
    case 'image': {
      if (value && !value.verticalAlignment) {
        value.verticalAlignment = String(changedSection.verticalAlignment).toUpperCase() || 'MIDDLE';
      }
      if (value && !value.horizontalAlignment) {
        value.horizontalAlignment = String(changedSection.horizontalAlignment).toUpperCase() || 'CENTER';
      }
      changedSection
        .setImage(value)
        .setText('')
        .setGlyph(null)
        .setCode(null)
        .setShape(null);
      break;
    }
    case 'angle': {
      const { page } = getAllLayers(changedSection.id, pages);
      let corrVal = value;
      if (page && page.transforms && (page.transforms.mirror_x || page.transforms.mirror_y)) {
        corrVal = -value;
      }
      let newAngle = (section.angle + corrVal) % 360;
      if (newAngle < 0) {
        newAngle = 360 + newAngle;
      }
      changedSection.setAngle(newAngle).setChanged(true);
      break;
    }
    case SECTION_MIRROR: {
      const { page } = getAllLayers(changedSection.id, pages);
      let corrVal = value;
      const additAngle = page?.transforms?.angle || 0;
      if (additAngle + changedSection.angle === 90 || additAngle + changedSection.angle === 270) {
        corrVal = corrVal === 'x' ? 'y' : 'x';
      }
      if (corrVal === 'x') {
        changedSection.setMirrorX(!section.mirrorX);
      } else {
        changedSection.setMirrorY(!section.mirrorY);
      }
      break;
    }
    case 'text': {
      changedSection
        .setImage(null)
        .setText(value)
        .setGlyph(null)
        .setCode(null)
        .setShape(null);
      break;
    }
    case 'shape': {
      changedSection
        .setImage(null)
        .setText('')
        .setGlyph(null)
        .setCode(null)
        .setShape(value);
      break;
    }
    case 'shapeStrokeColor': {
      changedSection.setShapeStokeColor(value);
      break;
    }
    case 'shapeFillColor': {
      changedSection.setShapeFillColor(value);
      break;
    }
    case 'graphicFormattingScale': {
      changedSection.setGraphicFormattingScale(value);
      break;
    }
    default:
      break;
  }

  return changedSection;
};

export const splitSection = type => (dispatch, getState) => {
  dispatch(createSnapshotThunk());

  const { core: { pages, pagesSelection, sectionsSelection } } = getState();
  const currentPage = pages[pagesSelection[0]];
  const sectionsSelectionIds = Object.keys(sectionsSelection);
  const notSelectedSections = Object.entries(currentPage.sections).reduce((acc, [key, value]) => {
    if (!sectionsSelectionIds.includes(key)) {
      return { ...acc, [key]: value };
    }
    return acc;
  }, {});

  switch (type) {
    case 'vertical': {
      const sections = Object.entries(sectionsSelection).reduce(
        (acc, [key, value]) => {
          const section = currentPage.sections[key];
          const [sectionLeft, sectionRight] = Section.fromDict(section).splitVertical();
          return {
            left: { ...acc.left, [sectionLeft.id]: sectionLeft },
            right: { ...acc.right, [sectionRight.id]: sectionRight },
          };
        },
        { left: {}, right: {} },
      );
      const updatedSections = { ...sections.left, ...sections.right };
      const updatedPage = { ...currentPage, sections: { ...notSelectedSections, ...updatedSections } };

      dispatch(splitSectionsAction({ page: updatedPage, sectionsSelection: sections.left }));
      break;
    }
    case 'horizontal': {
      const sections = Object.entries(sectionsSelection).reduce(
        (acc, [key, value]) => {
          const section = currentPage.sections[key];
          const [sectionTop, sectionBottom] = Section.fromDict(section).splitHorizontal();
          return {
            top: { ...acc.top, [sectionTop.id]: sectionTop },
            bottom: { ...acc.bottom, [sectionBottom.id]: sectionBottom },
          };
        },
        { top: {}, bottom: {} },
      );
      const updatedSections = { ...sections.top, ...sections.bottom };
      const updatedPage = { ...currentPage, sections: { ...notSelectedSections, ...updatedSections } };

      dispatch(splitSectionsAction({ page: updatedPage, sectionsSelection: sections.top }));
      break;
    }
    default: {
      break;
    }
  }
};

export const setLaserOutputQualityValueToSectionThunk = (section, value, isUndoable = true) => (dispatch, getState) => {
  const sectionToModify = Section.fromDict(section);

  sectionToModify.setLaserOutputQuality(value);

  dispatch(editSection(sectionToModify));
  dispatch(createSnapshotThunk({ isUndoable }));
};

export const updateSectionShapeProps = props => (dispatch, getState) => {
  const { core: { sectionsSelection, pages } } = getState();
  const selectedSection = getSelectedSection({ sectionsSelection, pages });

  if (selectedSection) {
    dispatch(updateSelectedSectionsProps({ shape: { ...selectedSection.shape, ...props } }));
  }
};

export const clearSelectedSections = () => {
  return createAction(CLEAR_SELECTED_SECTIONS, () => {})();
};

export const clearAllSelectedSections = () => {
  return createAction(CLEAR_ALL_SELECTED_SECTIONS, () => {})();
}

export const addSelectedSections = payload => {
  return createAction(ADD_SELECTED_SECTIONS, () => payload)();
};

export const addSelectedSectionThunk = section => (dispatch, getState) => {
  const { core: { sectionsSelection, shieldsSelection, pages } } = getState();
  const { shield } = getAllLayers(section.id, pages);

  if (!!sectionsSelection[section.id]) {
    const { [shield.id]: removedShield, ...restShields } = shieldsSelection;
    const { [section.id]: removedSection, ...restSections } = sectionsSelection;
    const isParentShieldUsed = Object.values(restSections).some(section => section.shieldId === shield.id);

    dispatch(addSelectedSections({
      sectionsSelection: restSections,
      shieldsSelection: isParentShieldUsed ? shieldsSelection : restShields,
      floatableInputVisible: false,
    }));
  } else {
    const shields = { ...shieldsSelection, [shield.id]: shield };
    const sections = { ...sectionsSelection, [section.id]: section };

    dispatch(addSelectedSections({
      sectionsSelection: sections,
      shieldsSelection: shields,
      floatableInputVisible: false,
    }));
  }
};

export const addMultipleSectionsThunk = payload => (dispatch, getState) => {
  const { selectedSections } = payload;
  const currentPageShields = getCurrentPageShields();
  const { sectionsSelection, shieldsSelection } = Object.values(selectedSections).reduce(
    (acc, section) => {
      return {
        ...acc,
        sectionsSelection: { ...acc.sectionsSelection, [section.id]: section },
        shieldsSelection: { ...acc.shieldsSelection, ...(!acc.shieldsSelection[section.shieldId] && { [section.shieldId]: currentPageShields[section.shieldId] }) },
      };
    },
    { sectionsSelection: {}, shieldsSelection: {} },
  );

  dispatch(addSelectedSections({ sectionsSelection, shieldsSelection }));
};

export const deleteSelectedSectionsThunk = (isUndoable = true) => (dispatch, getState) => {
  const { core: { pages, pagesSelection, sectionsSelection, shieldsSelection, ribbonColor } } = getState();

  dispatch(createSnapshotThunk({ isUndoable }));

  const currentPage = pages[pagesSelection[0]];
  const sectionsSelectionIds = Object.keys(sectionsSelection);
  const notSelectedSections = Object.entries(currentPage.sections).reduce((acc, [key, value]) => {
    if (!sectionsSelectionIds.includes(key)) {
      return { ...acc, [key]: value };
    }
    return acc;
  }, {});
  const notSelectedSectionsValues = Object.values(notSelectedSections);
  const newEmptySections = Object.values(shieldsSelection).reduce((acc, shield) => {
    if (notSelectedSectionsValues.find(section => section.shieldId === shield.id)) {
      return acc;
    }
    const pages = { [currentPage.uid]: currentPage };
    const section = createSectionSmart({ sourceShield: shield, pages, ribbonColor });
    return { ...acc, [section.id]: section };
  }, {});
  const sections = { ...notSelectedSections, ...newEmptySections };

  dispatch(
    deleteSelectedSectionsAction({
      page: { ...currentPage, sections },
      sectionsSelection: {},
      shieldsSelection: {},
    }),
  );
};

export const deleteSelectedSectionsContentThunk = (isUndoable = true) => (dispatch, getState) => {
  dispatch(createSnapshotThunk({ isUndoable }));

  const { core: { pages, pagesSelection, sectionsSelection } } = getState();
  const currentPage = pages[pagesSelection[0]];
  const sectionsSelectionIds = Object.keys(sectionsSelection);
  const notSelectedSections = Object.entries(currentPage.sections).reduce((acc, [key, value]) => {
    if (!sectionsSelectionIds.includes(key)) {
      return { ...acc, [key]: value };
    }
    return acc;
  }, {});
  const newEmptySections = Object.entries(currentPage.sections).reduce((acc, [key, value]) => {
    if (sectionsSelectionIds.includes(key)) {
      return {
        ...acc,
        [key]: Section.fromDict(value)
          .clearContent()
          .toDict(),
      };
    }
    return acc;
  }, {});
  const sections = { ...notSelectedSections, ...newEmptySections };

  dispatch(
    deleteSelectedSectionsAction({
      page: { ...currentPage, sections },
      sectionsSelection: {},
      shieldsSelection: {},
    }),
  );
};

export const deleteSectionThunk = () => (dispatch, getState) => {
  const { core: { pages, sectionsSelection } } = getState();
  const selectedSection = getSelectedSection({ sectionsSelection, pages });

  // handle matrix case (as before)
  if (selectedSection) {
    const { shield } = getAllLayers(selectedSection.id, pages);
    if (shield.isMatrix) {
      return dispatch(deleteSectionById(selectedSection.id, true));
    }
  }

  dispatch(deleteSelectedSectionsThunk());
};

export const deleteSelectedElementThunk = () => (dispatch, getState) => {
  const { core: { pages, sectionsSelection } } = getState();
  const selectedSection = getSelectedSection({ sectionsSelection, pages });

  // handle matrix case (as before)
  if (selectedSection) {
    const { shield } = getAllLayers(selectedSection.id, pages);
    if (shield.isMatrix) {
      return dispatch(deleteSelectedSections());
    }
  }

  dispatch(deleteSelectedSectionsThunk());
};

export const switchNextSectionByShieldThunk = () => (dispatch, getState) => {
  const {
    core: {
      shieldsSelection,
      sectionsSelection,
      pages,
      pagesSelection
    }
  } = getState();
  const currentPage = pages[pagesSelection[0]];

  if (currentPage) {
    const [selectedShield] = Object.values(shieldsSelection);
    const selectedSections = getSortedSectionsArray(
      getSelectedSections({
        sectionsSelection,
        page: currentPage,
      })
    );
    const [selectedSection] = selectedSections;

    if (selectedShield) {
      const pageSections = Object.values(currentPage.sections);
      const selectedShieldSections = pageSections.reduce(
        (acc, section) => {
          if (section.shieldId === selectedShield.id) {
            return { ...acc, [section.id]: section };
          }
          return acc;
        },
        {}
      );
      const sortedSelectedShieldSections = getSortedSectionsArray(selectedShieldSections);
      const curSectionIndex = sortedSelectedShieldSections.findIndex(
        section => section.id === selectedSection.id
      );
      const nextSectionIndex = curSectionIndex + 1;
      const nextSection = sortedSelectedShieldSections[nextSectionIndex];

      if (nextSection) {
        dispatch(handleSectionClick({ section: nextSection }));
      } else {
        const pageShields = getAllShields(currentPage);
        const shieldsToRender = Object.values(pageShields);
        const curShieldIndex = shieldsToRender.findIndex(
          item => item.id === selectedShield.id
        );
        const nextShield = shieldsToRender[curShieldIndex + 1] || shieldsToRender[0];
        const nextShieldSections = pageSections.reduce(
          (acc, section) => {
            if (section.shieldId === nextShield.id) {
              return { ...acc, [section.id]: section };
            }
            return acc;
          },
          {}
        );
        const sortedNextShieldSections = getSortedSectionsArray(nextShieldSections);
        const [nextShieldSection] = sortedNextShieldSections;
        if (nextShieldSection) {
          dispatch(handleSectionClick({ section: nextShieldSection }));
        }
      }
    }
  }
};

export const selectAllSectionsThunk = () => (dispatch, getState) => {
  const { core: { pages, pagesSelection } } = getState();
  const currentPage = pages[pagesSelection[0]];

  if (currentPage) {
    const sections = Object.values(currentPage.sections).reduce((acc, section) => {
      return { ...acc, [section.id]: section };
    }, {});
    const shields = getAllShields(currentPage);

    dispatch(addSelectedSections({
      sectionsSelection: sections,
      shieldsSelection: shields,
    }));
  }
};

const getBase64Image = async data => {
  const { settings, images } = data;
  const { pattern, value } = settings;
  const { source, current } = images;

  switch (pattern) {
    case 'CURRENT': {
      return { source, href: current };
    }
    case 'CONTRAST': {
      return { source, href: await convertImageToBlackAndWhite({ src: source, threshold: value }) };
    }
    case 'SOURCE':
    default: {
      return { source, href: source };
    }
  }
};

export const updateImageContentThunk = payload => async (dispatch, getState) => {
  const { isUndoable = false, settings, data } = payload;
  const { isNew } = settings;
  const { core: { pages, pagesSelection, sectionsSelection } } = getState();
  const currentPage = pages[pagesSelection[0]];

  if (currentPage) {
    dispatch(createSnapshotThunk({ isUndoable }));
    dispatch(setShowLoader(true));

    const selectedSectionIds = Object.keys(sectionsSelection);
    const updatedSections = {};

    for (const section of Object.values(currentPage.sections)) {
      if (isNew) {
        if (selectedSectionIds.includes(section.id)) {
          const images = { source: data.source, current: data.href };
          const base64Image = await getBase64Image({ settings, images });
          const image = { ...data, ...base64Image };

          updatedSections[section.id] = { ...Section.resetContent(section), image };
        }
      } else {
        if (selectedSectionIds.includes(section.id) && !!section.image) {
          const images = { source: section.image.source, current: section.image.href };
          const base64Image = await getBase64Image({ settings, images });
          const image = { ...data, ...base64Image };

          updatedSections[section.id] = { ...Section.resetContent(section), image };
        }
      }
    }

    dispatch(
      overridePageAction({
        page: { ...currentPage, sections: { ...currentPage.sections, ...updatedSections } },
        sectionsSelection: updatedSections,
      }),
    );
    dispatch(setShowLoader(false));
  }
};

export const switchNextSectionByArrowThunk = arrowDirection => (dispatch, getState) => {
  const { core: { shieldsSelection, sectionsSelection, pages, pagesSelection } } = getState();
  const currentPage = pages[pagesSelection[0]];

  if (currentPage) {
    const [selectedShield] = Object.values(shieldsSelection);
    const shields = getAllShields(currentPage);
    const shieldsToRender = Object.values(shields);
    const sectionsSelectionIds = Object.keys(sectionsSelection);
    const [selectedSectionId] = sectionsSelectionIds;
    const selectedSection = currentPage.sections[selectedSectionId];

    if (selectedShield) {
      if (selectedShield.isMatrix) {
        const shieldSections = Object.values(currentPage.sections).reduce((acc, section) => {
          if (section.shieldId === selectedShield.id) {
            return [...acc, section];
          }
          return acc;
        }, []);
        let nextSection = null;
        
        switch (arrowDirection) {
          case KEY_ARROW_LEFT: {
            const [candidate] = shieldSections.filter((section) => section.y === selectedSection.y && section.x < selectedSection.x).sort((a, b) => b.x - a.x);
            nextSection = candidate;
            break;
          }
          case KEY_ARROW_RIGHT: {
            const [candidate] = shieldSections.filter((section) => section.y === selectedSection.y && section.x > selectedSection.x).sort((a, b) => a.x - b.x);
            nextSection = candidate;
            break;
          }
          case KEY_ARROW_UP: {
            const [candidate1] = shieldSections.filter((section) => {
              return (section.x === selectedSection.x && section.y < selectedSection.y) && (section.width === selectedSection.width && section.height === selectedSection.height);
            }).sort((a, b) => b.y - a.y);
            const candidate2 = shieldSections.reverse().find(section => section.y < selectedSection.y && section.x <= selectedSection.x);
            nextSection = candidate1 || candidate2;
            break;
          }
          case KEY_ARROW_DOWN: {
            const [candidate1] = shieldSections.filter((section) => {
              return (section.x === selectedSection.x && section.y > selectedSection.y) && (section.width === selectedSection.width && section.height === selectedSection.height);
            }).sort((a, b) => a.y - b.y);
            const candidate2 = shieldSections.find(section => section.y > selectedSection.y && section.x >= selectedSection.x);
            nextSection = candidate1 || candidate2;
            break;
          }
          default: {
            break;
          }
        }

        if (nextSection) {
          dispatch(handleSectionClick({ section: nextSection }));
        }
      } else {
        const currentIndex = shieldsToRender.findIndex(shield => shield.id === selectedShield.id);
        const currentShield = shieldsToRender[currentIndex];
        let tempIndex = currentIndex;

        switch (arrowDirection) {
          case KEY_ARROW_LEFT: {
            if (currentIndex > 0) {
              if (shieldsToRender[currentIndex - 1].primitiveProps.y === currentShield.primitiveProps.y) {
                tempIndex--;
              } else {
                const shieldLeft = shieldsToRender.find((shield, index) => shield.primitiveProps.y === currentShield.primitiveProps.y && currentIndex !== index);
                if (shieldLeft) {
                  tempIndex = shieldsToRender.indexOf(shieldLeft);
                }
              }
            }
            break;
          }
          case KEY_ARROW_RIGHT: {
            if (currentIndex < shieldsToRender.length - 1) {
              if (shieldsToRender[currentIndex + 1].primitiveProps.y === currentShield.primitiveProps.y) {
                tempIndex++;
              } else {
                const shieldRight = shieldsToRender.find((shield, index) => shield.primitiveProps.y === currentShield.primitiveProps.y && index > currentIndex);
                if (shieldRight) {
                  tempIndex = shieldsToRender.indexOf(shieldRight);
                }
              }
            }
            break;
          }
          case KEY_ARROW_UP: {
            const shieldAbove = [...shieldsToRender].reverse().find(shield => shield.primitiveProps.x === currentShield.primitiveProps.x && shield.primitiveProps.y < currentShield.primitiveProps.y);
            if (shieldAbove) {
              tempIndex = shieldsToRender.indexOf(shieldAbove);
            }
            break;
          }
          case KEY_ARROW_DOWN: {
            const shieldBelow = shieldsToRender.find(shield => shield.primitiveProps.x === currentShield.primitiveProps.x && shield.primitiveProps.y > currentShield.primitiveProps.y);
            if (shieldBelow) {
              tempIndex = shieldsToRender.indexOf(shieldBelow);
            }
            break;
          }
          default: {
            break;
          }
        }

        if (currentIndex !== tempIndex) {
          const nextShield = shieldsToRender[tempIndex];
          const nextShieldSections = Object.values(currentPage.sections).reduce((acc, section) => {
            if (section.shieldId === nextShield.id) {
              return { ...acc, [section.id]: section };
            }
            return acc;
          }, {});
          const [nextShieldSection] = Object.values(nextShieldSections);
  
          dispatch(applyGlobalSectionSettingsToSection({ section: nextShieldSection, shield: nextShield }));
          dispatch(addSelectedSections({
            sectionsSelection: nextShieldSections,
            shieldsSelection: { [nextShield.id]: nextShield },
          }));
        }
      }
    }
  }
};

export const applyGlobalSectionSettingsToSection = ({ section, shield }) => (dispatch, getState) => {
  const { appSettingsReducer: { section: globalSectionSettings } } = getState();

  if (Section.contentIsEmpty(section) && !shield?.isMatrix) {
    dispatch(updateSectionProps(section, globalSectionSettings, false));
  }
};